import { apiFetch } from "./apiFetch";

type NullableBoolean = boolean | null | undefined;

export interface UserAdminInfo {
  id: string;
  fsId?: string;
  email: string;
  displayName: string;
  createdAtUtc: string;
  accountState: string;
  revenueCatId?: string;
  notificationsAllow: NullableBoolean;
  settingSharingEmailsIncludeCalendarInvites: NullableBoolean;
  settingSharingEmailsSelfWhenNotSharing: NullableBoolean;
  settingWeeklyEmailsEnabled: NullableBoolean;
  pushIds: string[];
}

export interface UsersListResponse {
  users: UserAdminInfo[];
  nextToken?: string;
}

/** Paid-access duration granted at creation. "none" grants nothing. */
export type EntitlementDuration =
  | "none"
  | "daily"
  | "three_day"
  | "weekly"
  | "monthly"
  | "three_month"
  | "six_month"
  | "yearly"
  | "lifetime";

export interface CreateUserResult {
  userId: string;
  revenueCatId: string;
  entitlementGranted: boolean;
}

export interface RcEntitlement {
  expires_date: string | null;
  product_identifier: string;
  purchase_date: string;
}

export interface RcSubscription {
  expires_date: string;
  period_type: "normal" | "intro" | "trial";
  store: string;
  is_sandbox: boolean;
  grace_period_expires_date: string | null;
  unsubscribe_detected_at: string | null;
  billing_issues_detected_at: string | null;
}

export interface UserRevenuecatInfo {
  subscriber: {
    entitlements: Record<string, RcEntitlement>;
    subscriptions: Record<string, RcSubscription>;
    first_seen: string;
    last_seen?: string;
    management_url?: string | null;
  } | null;
}

export const UserResource = {
  getList: (params?: { filter?: string; revenuecatId?: string; nextToken?: string }) =>
    apiFetch<UsersListResponse>("/admin/users", { searchParams: params }),

  sendPush: (userId: string, body: { title: string; body: string }) =>
    apiFetch<{ messageIds: string[] }>(`/admin/user/${userId}/send-push`, {
      method: "POST",
      body,
    }),

  resendActivation: (userId: string) =>
    apiFetch<{ id: string }>(`/admin/user/${userId}/resend-activation`, {
      method: "POST",
    }),

  createUser: (data: {
    email: string;
    displayName: string;
    entitlementDuration: EntitlementDuration;
  }) => apiFetch<CreateUserResult>("/admin/user", { method: "POST", body: data }),

  migrateEmail: (userId: string, newEmail: string) =>
    apiFetch<UserAdminInfo>(`/admin/user/${userId}/migrate-email`, {
      method: "POST",
      body: { new: newEmail },
    }),

  getRevenuecat: (userId: string) =>
    apiFetch<UserRevenuecatInfo>(`/admin/user/${userId}/revenuecat`),

  postReceipt: (userId: string, subscriptionId: string) =>
    apiFetch<object>(`/admin/user/${userId}/receipt`, {
      method: "POST",
      body: { subscriptionId },
    }),

  getUsersByRcProvider: (params: { store?: string; status?: string; filter?: string; nextToken?: string }) =>
    apiFetch<UsersListResponse>(`/admin/users/revenuecat`, {
      searchParams: params,
    }),
};
